import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Box, Sparkles, MapPin } from "lucide-react";
import { Header } from "@/components/Header";
import { CartSheet } from "@/components/CartSheet";

const IMG = {
  bridge: "https://images.unsplash.com/photo-1573261658953-8b29e144d1af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxicm9va2x5biUyMHN0cmVldCUyMGJyaWRnZSUyMG5ldyUyMHlvcmt8ZW58MHx8fHwxNzg2Mzc5OTk0fDA&ixlib=rb-4.1.0&q=85",
  skyline: "https://images.unsplash.com/photo-1599854171059-d91f0fee45cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxicm9va2x5biUyMHN0cmVldCUyMGJyaWRnZSUyMG5ldyUyMHlvcmt8ZW58MHx8fHwxNzg2Mzc5OTk0fDA&ixlib=rb-4.1.0&q=85",
  shelves: "https://images.pexels.com/photos/4914808/pexels-photo-4914808.jpeg",
  cleaning: "https://images.pexels.com/photos/4914582/pexels-photo-4914582.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  kit: "https://images.pexels.com/photos/9507137/pexels-photo-9507137.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  streetwear: "https://images.unsplash.com/photo-1532332248682-206cc786359f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxzdHJlZXR3ZWFyJTIwdXJiYW4lMjBmYXNoaW9ufGVufDB8fHx8MTc4NjM3OTk5NHww&ixlib=rb-4.1.0&q=85",
};

const MANIFESTO = [
  { n: "01", title: "Every pair is an asset.", body: "You didn't camp online for hours to let heat crease in a closet corner. Storage is strategy." },
  { n: "02", title: "Dust is the enemy.", body: "UV light yellows soles. Humidity warps leather. Our boxes and cases lock all of it out." },
  { n: "03", title: "Clean pairs hold value.", body: "Pre-treat before you wear. Clean after. Whiten when they fade. Resale-ready, always." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <CartSheet />

      <section className="relative min-h-[80vh] flex items-end overflow-hidden border-b-2 border-foreground" data-testid="landing-hero">
        <img src={IMG.bridge} alt="Manhattan Bridge, Brooklyn NY" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
        <div className="noise-overlay z-10" aria-hidden="true" />
        <div className="relative z-20 max-w-7xl mx-auto px-6 pb-16 pt-40 w-full">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="flex items-center gap-2 text-white/90 text-sm uppercase tracking-[0.3em] mb-6">
              <MapPin size={14} className="text-primary" /> Brooklyn, New York
            </p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-[1.02] max-w-3xl">
              BORN IN BROOKLYN<span className="text-primary">.</span><br />
              BUILT TO PROTECT<span className="text-primary">.</span>
            </h1>
            <p className="mt-6 text-white/80 text-base md:text-lg max-w-xl">
              KicksGuard Supply Company. Storage, display, and care systems made for collectors who treat their rotation like a portfolio.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/shop" data-testid="landing-shop-cta" className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 font-medium uppercase tracking-wider text-sm hover:bg-white hover:text-foreground transition-colors">
                Shop the Collection <ArrowRight size={16} />
              </Link>
              <a href="#manifesto" data-testid="landing-story-cta" className="inline-flex items-center gap-2 border border-white text-white px-8 py-4 font-medium uppercase tracking-wider text-sm hover:bg-white hover:text-foreground transition-colors">
                Our Story
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="bg-foreground text-white py-3 border-b border-border">
        <Marquee speed={40} gradient={false} aria-hidden="true">
          {Array(4).fill(null).map((_, i) => (
            <span key={i} className="font-display text-sm uppercase tracking-[0.25em] mx-8">
              Protect Your Investment <span className="text-primary mx-8">•</span> BK Est. 2026 <span className="text-primary mx-8">•</span> Premium Sneaker Care <span className="text-primary mx-8">•</span> Slide-Out Storage <span className="text-primary mx-8">•</span> Wall Display Cases <span className="text-primary mx-8">•</span>
            </span>
          ))}
        </Marquee>
      </div>

      <section id="manifesto" className="relative bg-foreground text-white overflow-hidden" data-testid="landing-manifesto">
        <img src={IMG.skyline} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <p className="text-primary text-sm uppercase tracking-[0.3em] mb-4">The KicksGuard Code</p>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight max-w-2xl">
            FROM THE STOOPS OF BED-STUY TO YOUR SHELF WALL.
          </h2>
          <p className="mt-6 text-white/70 max-w-2xl text-base md:text-lg">
            We started in a Brooklyn walk-up with 60 pairs and nowhere to put them. Milk crates crushed toe boxes. Sunlight cooked the icy soles. So we built the storage we couldn't find — and the care routine to match.
          </p>
          <div className="grid md:grid-cols-3 gap-px bg-white/20 border border-white/20 mt-16">
            {MANIFESTO.map((m, i) => (
              <motion.div
                key={m.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.5 }}
                className="bg-foreground p-10"
              >
                <span className="font-display font-bold text-primary text-4xl">{m.n}</span>
                <h3 className="font-display font-semibold text-lg mt-6">{m.title}</h3>
                <p className="text-sm text-white/60 mt-3 leading-relaxed">{m.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="landing-categories">
        <div className="flex items-end justify-between gap-6 mb-12">
          <h2 className="font-display font-bold text-3xl tracking-tight">WHAT WE SUPPLY<span className="text-primary">.</span></h2>
          <Link to="/shop" data-testid="landing-view-all" className="text-sm uppercase tracking-wider border-b-2 border-foreground hover:border-primary hover:text-primary transition-colors pb-1">
            View All
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { img: IMG.shelves, label: "Storage Boxes", desc: "Stackable plastic, LED slide-outs, custom wood.", to: "/storage", testid: "category-teaser-storage" },
            { img: IMG.kit, label: "Shoe Care", desc: "Cleaners, pre-treatments, whiteners.", to: "/care", testid: "category-teaser-care" },
            { img: IMG.cleaning, label: "Display Cases", desc: "Wall-mounted cases that turn pairs into art.", to: "/storage", testid: "category-teaser-display" },
          ].map((c, i) => (
            <motion.div key={c.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}>
              <Link to={c.to} data-testid={c.testid} className="group block border-2 border-foreground">
                <div className="aspect-[4/3] overflow-hidden bg-secondary">
                  <img src={c.img} alt={c.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6 border-t-2 border-foreground flex items-center justify-between">
                  <div>
                    <h3 className="font-display font-semibold text-lg">{c.label}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{c.desc}</p>
                  </div>
                  <ArrowRight size={18} className="shrink-0 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-primary" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-t-2 border-foreground" data-testid="landing-brand-strip">
        <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
          <div className="border-2 border-foreground">
            <img src={IMG.streetwear} alt="Brooklyn streetwear" className="w-full max-h-[70vh] object-cover" />
          </div>
          <div>
            <p className="text-primary text-sm uppercase tracking-[0.3em] mb-4">Why We Exist</p>
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
              YOUR GRAILS AREN'T SHOES. THEY'RE HOLDINGS.
            </h2>
            <div className="mt-8 space-y-6">
              {[
                { icon: ShieldCheck, t: "Crush-tested, UV-blocked, dust-sealed", d: "Storage engineered to keep deadstock looking deadstock." },
                { icon: Box, t: "Built for every setup", d: "From a 10-pair starter stack to a full wall display flex." },
                { icon: Sparkles, t: "Care that holds resale value", d: "Collector-grade cleaners and treatments, no gimmicks." },
              ].map((f) => (
                <div key={f.t} className="flex gap-4">
                  <f.icon size={22} className="text-primary shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display font-semibold">{f.t}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/shop" data-testid="landing-bottom-cta" className="inline-flex items-center gap-2 mt-10 bg-foreground text-white px-8 py-4 font-medium uppercase tracking-wider text-sm hover:bg-primary transition-colors">
              Start Protecting <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t-2 border-foreground bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row justify-between gap-6">
          <div>
            <p className="font-display font-bold tracking-tight">KICKSGUARD<span className="text-primary">.</span>SUPPLY</p>
            <p className="text-sm text-white/60 mt-2">Brooklyn, NY — Protect your investment. Every pair, every day.</p>
          </div>
          <p className="text-xs text-white/40 self-end">© 2026 KicksGuard Supply Company</p>
        </div>
      </footer>
    </div>
  );
}
